import type { ProductWithDetails } from '@ab-core/graphql/dist';
import { useGetProductBySkuV2, useProductsByIdsV2 } from '@ab-core/hooks-v2';
import type { DefaultScalableProductCardProps } from '@ab-core/scalable-product-card';
import { useMappedProductToScalableProductV2 } from '@ab-core/scalable-product-card';
import { useMemo, useRef } from 'react';
import { useAccessoriesBarState } from '../state';

export type AccessoryProduct = DefaultScalableProductCardProps['product'];

type UseAddedToCartAccessoriesProductListResult = {
    productSku?: string;
    products: Array<AccessoryProduct>;
    loading: boolean;
};

const MAX_NECESSARY = 3;
const MAX_PRODUCTS = 8;
const MIN_AVAILABLE_QUANTITY = 1;

const EMPTY_PRODUCTS: Array<AccessoryProduct> = [];

const toStringIds = (ids: Array<unknown> | null | undefined): Array<string> =>
    Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
const filterAvailableProducts = (products: Array<ProductWithDetails>): Array<ProductWithDetails> =>
    products.filter((product: ProductWithDetails) => product.isOnlineAvailable === true && !product.procuredProduct);

const filterSufficientStock = (products: Array<AccessoryProduct>) => {
    return products.filter(
        (product) => (product.availability?.availability?.aggregatedMainChannelQuantity ?? 0) >= MIN_AVAILABLE_QUANTITY
    );
};

// A `productsByIdsV2` query has settled for the ids we currently care about when it is no longer loading and every returned result actually belongs to the requested id set.
const idsResolved = (
    requestedIds: Array<string>,
    results: Array<{ id?: string | null } | null | undefined> | undefined,
    loading: boolean,
    error: boolean
): boolean => {
    if (requestedIds.length === 0 || error) {
        return true;
    }

    if (loading) {
        return false;
    }

    return (results ?? []).every((entry) => !!entry?.id && requestedIds.includes(entry.id));
};

const selectAccessoryProducts = (
    necessaryProducts: Array<AccessoryProduct>,
    accessoryProducts: Array<AccessoryProduct>
): Array<AccessoryProduct> => {
    const seen = new Set<string>();
    const selectedProducts: Array<AccessoryProduct> = [];

    [...necessaryProducts.slice(0, MAX_NECESSARY), ...accessoryProducts].forEach((product) => {
        if (selectedProducts.length >= MAX_PRODUCTS || !product.sku || seen.has(product.sku)) {
            return;
        }

        seen.add(product.sku);
        selectedProducts.push(product);
    });

    return selectedProducts;
};

export const useAddedToCartAccessoriesProductList = (): UseAddedToCartAccessoriesProductListResult => {
    const currentSku = useAccessoriesBarState();
    const hasSku = !!currentSku.currentSku;

    const {
        product: boughtProduct,
        loading: boughtLoading,
        error: boughtError
    } = useGetProductBySkuV2({
        sku: currentSku.currentSku,
        skip: !currentSku.currentSku
    });
    // `useGetProductBySkuV2` keeps returning the last product when skipped, so only trust
    // `relatedProducts` when the fetched product matches the currently requested SKU
    const relatedProducts = boughtProduct?.sku === currentSku.currentSku ? boughtProduct?.relatedProducts : undefined;

    const necessaryIds = toStringIds(relatedProducts?.necessaryAccessories);
    const accessoryIds = toStringIds(relatedProducts?.accessoriesProducts);

    const {
        products: necessaryResult,
        loading: necessaryLoading,
        error: necessaryError
    } = useProductsByIdsV2(necessaryIds);
    const {
        products: accessoryResult,
        loading: accessoryLoading,
        error: accessoryError
    } = useProductsByIdsV2(accessoryIds);

    const necessaryRawProducts = necessaryIds.length > 0 ? (necessaryResult?.results ?? []).filter(Boolean) : [];
    const accessoryRawProducts = accessoryIds.length > 0 ? (accessoryResult?.results ?? []).filter(Boolean) : [];

    const necessaryMapped = useMappedProductToScalableProductV2(filterAvailableProducts(necessaryRawProducts));
    const accessoryMapped = useMappedProductToScalableProductV2(filterAvailableProducts(accessoryRawProducts));

    const selectedProducts = selectAccessoryProducts(
        filterSufficientStock(necessaryMapped),
        filterSufficientStock(accessoryMapped)
    );

    // We do some involved logic here that will make sure the state provided by this hook is stable, so only changes when the chain of hooks is actually done for the provided SKU.
    const boughtDone = !hasSku || boughtError || (!boughtLoading && boughtProduct?.sku === currentSku.currentSku);
    const necessaryDone = idsResolved(necessaryIds, necessaryResult?.results, necessaryLoading, necessaryError);
    const accessoryDone = idsResolved(accessoryIds, accessoryResult?.results, accessoryLoading, accessoryError);
    const ready = boughtDone && necessaryDone && accessoryDone;
    const productsKey = ready ? selectedProducts.map((product) => product.sku).join(',') : '';
    const committedRef = useRef<{ key: string; products: Array<AccessoryProduct> }>({
        key: '',
        products: EMPTY_PRODUCTS
    });

    if (ready && productsKey !== committedRef.current.key) {
        committedRef.current = { key: productsKey, products: selectedProducts };
    }

    const products = committedRef.current.products;
    const loading = !ready;

    return useMemo(
        () => ({
            productSku: currentSku.currentSku,
            products,
            loading
        }),
        [currentSku.currentSku, products, loading]
    );
};
